// supabaseClient.js
import { createClient } from '@supabase/supabase-js'
import { fetchFromAPI } from './common.js';

const supabaseUrl = "https://cnpozvvusjsxkydugtlg.supabase.co"
const supabaseAnonKey = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImNucG96dnZ1c2pzeGt5ZHVndGxnIiwicm9sZSI6ImFub24iLCJpYXQiOjE2OTkyOTUxMTYsImV4cCI6MjAxNDg3MTExNn0.bRrcrO1pbKmIF7KXDJn03pTSQhx9VxYA4hGFnbFY3UA'

export const supabase = createClient(supabaseUrl, supabaseAnonKey)


const writeToDatabase = async (email, practiceId, firstName, lastName, bAdminUser, bNonVoipOnboarding=false) => {
  try {
    console.log("writeToDatabase ", email, practiceId, firstName, lastName, bAdminUser)
    return await fetchFromAPI('/onboard/users', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email, practiceId, firstName, lastName, bAdminUser, bNonVoipOnboarding }),
    });
  } catch (error) {
    console.error('Error in writeToDatabase:', error);
    throw error;
  }
};

// signUp function to be used in your React components


export const signIn = async (email, password) => {
  try {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    console.log("signIn result:", data, error);
    if (error) throw error;

    // Check if user signed in successfully
    if (data.session && data.user) {
      // Check the Authentication Assurance Level (AAL) to determine if MFA is required
      const { data: aalData, error: aalError } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
      
      if (aalError) {
        console.error('Error getting AAL:', aalError);
        // If we can't get AAL, assume no MFA for now
        return { session: data.session, user: data.user, requiresMFA: false };
      }

      const { currentLevel, nextLevel } = aalData;
      console.log('AAL levels:', { currentLevel, nextLevel });

      // If current level is aal1 and next level is aal2, MFA is required
      if (currentLevel === 'aal1' && nextLevel === 'aal2') {
        return { 
          session: data.session, // Session exists but with limited access
          user: data.user, 
          requiresMFA: true,
          currentAAL: currentLevel,
          requiredAAL: nextLevel
        };
      } else {
        // MFA not required or already verified
        return { 
          session: data.session, 
          user: data.user, 
          requiresMFA: false,
          currentAAL: currentLevel,
          requiredAAL: nextLevel 
        };
      }
    }

    // No session created
    throw new Error('Authentication failed - no session created');
  } catch (error) {
    console.error('Error signing in:', error);
    throw error;
  }
};

// Sign in with MFA verification
export const signInWithMFA = async (mfaCode) => {
  try {
    console.log('Starting MFA verification with code:', mfaCode?.replace(/./g, '*'));
    
    // Check current session status
    const { data: { session: currentSession } } = await supabase.auth.getSession();
    console.log('Current session exists:', !!currentSession);
    
    // Validate the MFA code format
    if (!mfaCode || mfaCode.length !== 6 || !/^\d{6}$/.test(mfaCode)) {
      throw new Error('MFA code must be exactly 6 digits');
    }

    // Get the user's MFA factors (user should already be signed in with AAL1)
    const { data: factors, error: factorsError } = await supabase.auth.mfa.listFactors();
    if (factorsError) {
      console.error('Error listing MFA factors:', factorsError);
      throw factorsError;
    }

    console.log('Available MFA factors:', factors);

    // Find the verified TOTP factor
    const totpFactor = factors.totp?.find(factor => factor.status === 'verified');
    if (!totpFactor) {
      console.error('No verified TOTP factor found. Available factors:', factors);
      throw new Error('No verified MFA factor found. Please set up 2FA first.');
    }

    console.log('Using TOTP factor:', { id: totpFactor.id, friendlyName: totpFactor.friendly_name });

    // Use two-step challenge and verify process for more reliable flow
    console.log('Challenging factor:', totpFactor.id);
    const { data: challenge, error: challengeError } = await supabase.auth.mfa.challenge({
      factorId: totpFactor.id
    });
    
    if (challengeError) {
      console.error('Challenge error:', challengeError);
      throw challengeError;
    }
    
    console.log('Challenge successful, now verifying with code');
    const { data: verifyData, error: verifyError } = await supabase.auth.mfa.verify({
      factorId: totpFactor.id,
      challengeId: challenge.id,
      code: mfaCode
    });

    if (verifyError) {
      console.error('MFA verification error:', verifyError);
      
      // Provide more specific error messages
      if (verifyError.message?.includes('Invalid TOTP code')) {
        throw new Error('Invalid 2FA code. Please check the code and try again. Make sure your device time is correct.');
      } else if (verifyError.message?.includes('TOTP code expired')) {
        throw new Error('2FA code has expired. Please use a fresh code from your authenticator app.');
      } else {
        throw new Error(`MFA verification failed: ${verifyError.message}`);
      }
    }

    console.log('MFA verification successful:', { 
      hasSession: !!verifyData.session, 
      hasUser: !!verifyData.user,
      hasAccessToken: !!verifyData.access_token
    });
    console.log('Full verifyData response:', verifyData);
    
    // Construct session object from the verification response
    const session = verifyData.session || (verifyData.access_token ? {
      access_token: verifyData.access_token,
      refresh_token: verifyData.refresh_token,
      expires_in: verifyData.expires_in,
      expires_at: verifyData.expires_at,
      token_type: verifyData.token_type || 'bearer'
    } : null);
    
    const user = verifyData.user;
    
    console.log('Constructed session:', !!session);
    console.log('Extracted user:', !!user);

    // After successful MFA verification, check AAL again
    const { data: aalData, error: aalError } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
    if (aalError) {
      console.error('Error getting AAL after MFA:', aalError);
    }

    console.log('AAL after MFA verification:', aalData);

    return { 
      session: session, 
      user: user, 
      requiresMFA: false,
      currentAAL: aalData?.currentLevel,
      requiredAAL: aalData?.nextLevel
    };
  } catch (error) {
    console.error('Error in signInWithMFA:', error);
    
    // Re-throw with more user-friendly message if needed
    if (error.message?.includes('Invalid TOTP') || error.message?.includes('Invalid 2FA code')) {
      throw error; // Already has a good message
    } else {
      throw new Error(`MFA verification failed: ${error.message}`);
    }
  }
};


  export const signUp = async (email, password, practiceId, firstName, lastName, bAdminUser, bNonVoipOnboarding) => {
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            firstName,
            lastName,
            practices: [{ practiceId, role: 'user' }],
            bAdminUser,
            bNonVoipOnboarding,
          },
        },
      });
  
      if (error) {
        throw error;
      }
  
      console.log(data); // Log data to see what is returned
  
      // Assuming data contains user information, and you need the user's ID
      // Make sure to check the structure of the returned data object
      
      if (data.user) {
        // send the confirm email 
        // const { error: emailError } = await supabase.auth.api.sendMagicLinkEmail(email);
        // if (emailError) {
        //   console.error('Error sending confirmation email:', emailError);
        //   // Handle the error appropriately
        // } else {
        //   console.log('Confirmation email sent successfully');
        // }

        await writeToDatabase(email, practiceId, firstName, lastName, bAdminUser, bNonVoipOnboarding);
      }  
  
      return { data };
    } catch (error) {
      console.error('Error signing up:', error);
      throw error; // Re-throw the error so that it can be caught and handled by the caller
    }
  };
  
  export const updatePassword = async (newPassword) => {
    const { data, error } = await supabase.auth.updateUser({ password: newPassword });
    return { data, error };
  };

  export const resetPassword = async (email) => {
    const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: 'https://app.joinnewton.com/updatepassword', // Update with your password update page URL
    });
    return { data, error };
};

// ========== TWO-FACTOR AUTHENTICATION FUNCTIONS ==========

// Enroll a factor for MFA
export const enrollMFA = async (factorType = 'totp', issuer = 'Newton') => {
  try {
    // First check for existing factors
    const existingFactors = await getMFAFactors();
    
    // If there are existing unverified factors, unenroll them first
    if (existingFactors.totp && existingFactors.totp.length > 0) {
      for (const factor of existingFactors.totp) {
        if (factor.status === 'unverified') {
          try {
            await unenrollMFA(factor.id);
          } catch (cleanupError) {
            console.warn('Failed to cleanup unverified factor:', cleanupError);
          }
        }
      }
    }

    // Create a unique friendly name with timestamp
    const friendlyName = `Newton-TOTP-${Date.now()}`;
    
    const { data, error } = await supabase.auth.mfa.enroll({
      factorType,
      issuer,
      friendlyName,
    });
    
    if (error) throw error;
    
    return {
      id: data.id,
      qrCode: data.totp?.qr_code,
      secret: data.totp?.secret,
      uri: data.totp?.uri,
      friendlyName,
    };
  } catch (error) {
    console.error('Error enrolling MFA:', error);
    throw error;
  }
};

// Verify and enable a factor
export const verifyMFA = async (factorId, code) => {
  try {
    const { data, error } = await supabase.auth.mfa.challengeAndVerify({
      factorId,
      code,
    });
    
    if (error) throw error;
    
    return data;
  } catch (error) {
    console.error('Error verifying MFA:', error);
    throw error;
  }
};

// Get all enrolled factors
export const getMFAFactors = async () => {
  try {
    const { data, error } = await supabase.auth.mfa.listFactors();
    
    if (error) throw error;
    
    return data;
  } catch (error) {
    console.error('Error getting MFA factors:', error);
    throw error;
  }
};

// Unenroll/disable a factor
export const unenrollMFA = async (factorId) => {
  try {
    const { data, error } = await supabase.auth.mfa.unenroll({
      factorId,
    });
    
    if (error) throw error;
    
    return data;
  } catch (error) {
    console.error('Error unenrolling MFA:', error);
    throw error;
  }
};

// Challenge a factor (for verification during login or sensitive operations)
export const challengeMFA = async (factorId) => {
  try {
    const { data, error } = await supabase.auth.mfa.challenge({
      factorId,
    });
    
    if (error) throw error;
    
    return data;
  } catch (error) {
    console.error('Error challenging MFA:', error);
    throw error;
  }
};

// Verify a challenge
export const verifyMFAChallenge = async (factorId, challengeId, code) => {
  try {
    const { data, error } = await supabase.auth.mfa.verify({
      factorId,
      challengeId,
      code,
    });
    
    if (error) throw error;
    
    return data;
  } catch (error) {
    console.error('Error verifying MFA challenge:', error);
    throw error;
  }
};

// Get current user's AAL (Authentication Assurance Level)
export const getAuthenticationLevel = async () => {
  try {
    const { data, error } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
    
    if (error) throw error;
    
    return data;
  } catch (error) {
    console.error('Error getting authentication level:', error);
    throw error;
  }
};

